/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import { SearchList } from '../../../common/component/SearchList'
import {
  useAssetGetByIdMutation,
  useAssetLoadMoreMutation,
  useAssetSearchMutation,
} from '../Asset.api'
import { useAppDispatch, useAppSelector } from '../../store/Store'
import {
  selectAsset,
  selectAssetFiltersSelection,
  selectAssetLoading,
  selectAssets,
  selectAssetsPagination,
  setAssetFiltersSelection,
} from '../Asset.slice'
import { AssetListItem } from '../components/AssetListItem'
import { AssetPreview } from '../components/AssetPreview'
import { IAssetFilter } from '../Asset.model'
import { IFilterDto } from '../../../common/model/Core.model'
import { TagInputSearchControls } from '../../../common/component/TagInputSearchControls'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { useIntl } from 'react-intl'
import { getEntry, removeEntry, setEntry } from '../../../service/LocalStorage.service'

const ASSET_FILTERS_LS_KEY = 'asset_filtersSelection'

const AssetDashboard: FC = () => {
    const intl = useIntl()
    const breadCrumbs: Array<PageLink> = [
      {
        title: intl.formatMessage({id: 'BREADCRUMBS.ASSETS'}),
        path: '/assets',
        isSeparator: false,
        isActive: false,
      },
    ]

    const didFetchInitialData = useRef(false)
    const didFetchNextPage = useRef(false)
    const didFetchSearch = useRef(false)

    const filterKeys = {
      defaultKey: IAssetFilter.CLIENT,
      defaultKeyList: [
        IAssetFilter.ID,
        IAssetFilter.CLIENT,
        IAssetFilter.CONTRACT_ID,
        // IAssetFilter.GUI,
        IAssetFilter.FAMILY,
        IAssetFilter.AREA,
        IAssetFilter.BRAND,
        IAssetFilter.MODEL,
        IAssetFilter.SERIAL,
        IAssetFilter.ADDRESS,
      ],
    }
    const [isSearchLoading, setIsSearchLoading] = useState(false)
    const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
    const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
    const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectAssetFiltersSelection)

    const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
      if (!Array.isArray(candidateFilters)) {
        return []
      }
      return candidateFilters.filter((item): item is IFilterDto => {
        if (!item || typeof item !== 'object') {
          return false
        }
        const filter = item as IFilterDto
        return (
          typeof filter.id === 'string' &&
          filterKeys.defaultKeyList.includes(filter.id as IAssetFilter) &&
          typeof filter.value === 'string' &&
          typeof filter.editable === 'boolean'
        )
      })
    }

    const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
      dispatch(setAssetFiltersSelection(nextFilters))
      if (nextFilters.length > 0) {
        setEntry(ASSET_FILTERS_LS_KEY, JSON.stringify(nextFilters))
      } else {
        removeEntry(ASSET_FILTERS_LS_KEY)
      }
    }

    const setFilters = (nextFilters: IFilterDto[]) => {
      persistFiltersSelection(nextFilters)
    }
    
    const [assetSearch] = useAssetSearchMutation()
    const [assetLoadMore] = useAssetLoadMoreMutation()
    const [assetGetById] = useAssetGetByIdMutation()

    const assets = useAppSelector(selectAssets)
    const assetsPagination = useAppSelector(selectAssetsPagination)
    const asset = useAppSelector(selectAsset)
    const assetLoading = useAppSelector(selectAssetLoading)

    useEffect(() => {
      let initialFilters: IFilterDto[] | undefined
      const savedFilters = getEntry(ASSET_FILTERS_LS_KEY)
      if (savedFilters) {
        try {
          const parsedFilters = JSON.parse(savedFilters)
          const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
          persistFiltersSelection(sanitizedFilters)
          initialFilters = sanitizedFilters
        } catch {
          persistFiltersSelection([])
        }
      }

      if (initialFilters && initialFilters.length > 0) {
        const filterIds = initialFilters.map((filter) => filter.id)
        setFilterSuggestions(
          filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
        )
      } else {
        setFilterSuggestions(filterKeys.defaultKeyList)
      }

      fetchInitialData(initialFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchInitialData = async (filtersOverride?: IFilterDto[]) => {
      const requestFilters = filtersOverride ?? filters
      try {
        setIsSearchLoading(true)
        if (!didFetchInitialData.current) {
          const request: any = {}
          if (requestFilters.length > 0) {
            request.filters = requestFilters
          }
          const response: any = await assetSearch(request)
          if (response.data) {
            setIsSearchControlEnabled(true)
            if (response.data.filters) {
              const filterIds = response.data.filters.map((filter) => filter.id)
              persistFiltersSelection(response.data.filters)
              setFilterSuggestions(filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey)))
            } else {
              const filterIds = requestFilters.map((filter) => filter.id)
              setFilterSuggestions(filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey)))
            }
          }
        }
      } finally {
        setIsSearchLoading(false)
      }
    }

    const nextPageOnClickHandler = async () => {
      try {
        setIsSearchLoading(true)
        if (!didFetchNextPage.current) {
          await assetLoadMore({
            pagination: {
              limit: assetsPagination?.limit,
              sort: assetsPagination?.sort,
              page: assetsPagination?.page,
            },
            filters: filters,
          })
        }
      } finally {
        setIsSearchLoading(false)
      }
    }

    const searchOnClickHandler = async () => {
      try {
        setIsSearchLoading(true)
        if (!didFetchSearch.current) {
          await assetSearch({
            pagination: {
              sort: assetsPagination?.sort,
            },
            filters: filters,
          })
        }
      } finally {
        setIsSearchLoading(false)
      }
    }

    const resetSearchOnClickHandler = async () => {
      persistFiltersSelection([])
      setFilterSuggestions(filterKeys.defaultKeyList)
      await fetchInitialData([])
    }

    const rowOnClickHandler = async (id: string) => {
      setRowSelected(id)
      if (!asset || asset.id !== id) {
        await assetGetById({id, fields: 'LIMITED'})
      }
    }

    return (
      <>
        <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
        <div className='flex mt-10'>
          <div className='row g-5'>
            <SearchList
              loading={isSearchLoading}
              rowSelected={rowSelected}
              nextPage={assetsPagination?.page}
              nextPageOnClickHandler={nextPageOnClickHandler}
              searchControlsComponent={
                <TagInputSearchControls
                  loading={isSearchLoading}
                  enabled={isSearchControlEnabled}
                  filterKeys={filterKeys}
                  filterSuggestions={filterSuggestions}
                  filters={filters}
                  setFilterSuggestions={setFilterSuggestions}
                  setFilters={setFilters}
                  createOnClickPath='/assets/new'
                  createOnClickOpenNew={false}
                  searchOnClickHandler={searchOnClickHandler}
                  resetSearchResetOnClickHandler={resetSearchOnClickHandler}
                ></TagInputSearchControls>
              }
              listComponent={assets.map((item, index) => (
                <div key={index}>
                  <AssetListItem
                    data={item}
                    rowSelected={rowSelected}
                    onRowClick={rowOnClickHandler}
                  ></AssetListItem>
                  {index !== 0 && <div className='separator'></div>}
                </div>
              ))}
            ></SearchList>

            {rowSelected && (
              <AssetPreview
                asset={asset}
                loading={assetLoading}
                setRowSelected={setRowSelected}
              ></AssetPreview>
            )}
          </div>
        </div>
      </>
    )
}

export {AssetDashboard}
